import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getRecentNotifications } from '../../services/DashboardServices';
import '../../components/ActivityNotifications.css';

function PendingCheckInListModal({
  open,
  onClose,
  cookies,
  plantId,
  onSelect,
}) {
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const pendingTotal = useMemo(
    () => (groups || []).reduce((sum, group) => sum + (group.count || group.vehicles?.length || 0), 0),
    [groups],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError('');
    getRecentNotifications(cookies, plantId, 20)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setGroups(data?.pending_checkins || []);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setGroups([]);
        setLoadError(error?.response?.data?.detail || error?.message || 'Failed to load pending check-ins');
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, cookies, plantId]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const selectVehicle = (vehicle) => {
    if (!vehicle?.gmm_id || !onSelect) {
      return;
    }
    onSelect(vehicle.gmm_id);
  };

  const modal = (
    <>
      <div className="pending-checkin-card-backdrop" onClick={onClose} />
      <div
        className="modal pending-checkin-card-modal show"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ display: 'block' }}
        onClick={onClose}
      >
        <div className="modal-dialog" onClick={(event) => event.stopPropagation()}>
          <div className="modal-content pending-checkin-card-content">
            <div className="activity-notifications pending-checkin-card-panel">
              <div className="activity-notifications-header">
                <div className="activity-notifications-title">
                  <span className="activity-notifications-live" aria-hidden="true" />
                  <strong>Pending check-in</strong>
                  <span className="activity-notifications-count">{pendingTotal}</span>
                </div>
                <button
                  type="button"
                  className="activity-notifications-arrow"
                  onClick={onClose}
                  title="Close"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="activity-notifications-body">
                {isLoading ? (
                  <p className="activity-notifications-empty">Loading pending check-ins...</p>
                ) : loadError ? (
                  <p className="activity-notifications-empty">{loadError}</p>
                ) : pendingTotal === 0 ? (
                  <p className="activity-notifications-empty">No pending check-in vehicles.</p>
                ) : (
                  groups.map((group) => (
                    <div className="activity-pending-section" key={group.plant_id}>
                      <div className="activity-pending-heading">
                        Pending check-in
                        <span className="activity-pending-heading-count">
                          {group.count || group.vehicles?.length || 0}
                        </span>
                      </div>
                      <div className="activity-pending-plant">
                        <div className="activity-pending-plant-name">
                          {group.plant_name}
                          <span>{group.count || group.vehicles?.length || 0}</span>
                        </div>
                        <ul className="activity-notifications-list activity-pending-list">
                          {(group.vehicles || []).map((vehicle) => (
                            <li
                              key={vehicle.id || vehicle.gmm_id}
                              className="activity-notifications-item notif-kind-waiting pending-checkin-card-item"
                              onClick={() => selectVehicle(vehicle)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  selectVehicle(vehicle);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <span className="activity-notifications-badge">Pending</span>
                              <span className="activity-notifications-message">
                                {vehicle.message || vehicle.vehicle_no}
                                {vehicle.dc_no ? ` · DC ${vehicle.dc_no}` : ''}
                              </span>
                              <span className="activity-notifications-module">GATE</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}

export default PendingCheckInListModal;
